/**
 * Posts Context
 * Provides LinkedIn posts data to components throughout the app
 * Includes caching to prevent unnecessary API calls
 */
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { fetchLinkedInPosts } from "../services/api.service";

const PostsContext = createContext();

// Cache configuration
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds

// In-memory cache
let postsCache = null;
let cacheTimestamp = null;

export const usePosts = () => {
  const context = useContext(PostsContext);
  if (!context) {
    throw new Error("usePosts must be used within a PostsProvider");
  }
  return context;
};

export const PostsProvider = ({ children }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isFetchingRef = useRef(false);

  const getLinkedPosts = async () => {
    // Check cache first
    const now = Date.now();
    if (postsCache && cacheTimestamp && (now - cacheTimestamp) < CACHE_DURATION) {
      setPosts(postsCache);
      setLoading(false);
      setError(null);
      return;
    }

    // Prevent duplicate requests
    if (isFetchingRef.current) {
      return;
    }

    isFetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const result = await fetchLinkedInPosts();
      const postsData = result.data;
      
      // Update cache
      postsCache = postsData;
      cacheTimestamp = now;
      
      setPosts(postsData);
      setLoading(false);
      setError(null);
    } catch (err) {
      let errorMessage = "Unable to load posts at this time. Please try again later.";
      
      if (err.response) {
        // Server responded with error status
        if (err.response.status === 404) {
          errorMessage = "Posts endpoint not found. Please contact support if this persists.";
        } else if (err.response.status >= 500) {
          errorMessage = "Server error occurred. Please try again in a few moments.";
        } else if (err.response.status === 401 || err.response.status === 403) {
          errorMessage = "Access denied. Please refresh the page.";
        }
      } else if (err.request) {
        // Request made but no response received
        errorMessage = "Network error. Please check your internet connection and try again.";
      }
      
      setError(errorMessage);
      setLoading(false);
    } finally {
      isFetchingRef.current = false;
    }
  };

  useEffect(() => {
    getLinkedPosts();
  }, []);

  const retry = () => {
    getLinkedPosts();
  };

  const value = {
    posts,
    loading,
    error,
    retry,
  };

  return (
    <PostsContext.Provider value={value}>
      {children}
    </PostsContext.Provider>
  );
};
